import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'motion/react';
import type { WidgetId, GlobalAnalyticsResponse } from '@/types/analytics';
import { getWidgetSpan } from './widgetRegistry';
import { OverallSuccessRate } from './widgets/performance/OverallSuccessRate';
import { PerformanceByDifficulty } from './widgets/performance/PerformanceByDifficulty';
import { PerformanceByType } from './widgets/performance/PerformanceByType';
import { ImprovementTrends } from './widgets/performance/ImprovementTrends';
import { MostChallenging } from './widgets/questions/MostChallenging';
import { QuestionsMastered } from './widgets/questions/QuestionsMastered';
import { CalendarHeatmap } from './widgets/engagement/CalendarHeatmap';
import { DailyKnowledgeStreak } from './widgets/engagement/DailyKnowledgeStreak';
import { SessionsCompleted } from './widgets/engagement/SessionsCompleted';
import { TotalQuestionsAnswered } from './widgets/engagement/TotalQuestionsAnswered';
import { TotalStudyTime } from './widgets/engagement/TotalStudyTime';
import { QuizletMasteryHeatmap } from './widgets/quizlets/QuizletMasteryHeatmap';
import { BestWorstPerforming } from './widgets/quizlets/BestWorstPerforming';
import { TimeEfficiencyWidget } from './widgets/performance/TimeEfficiencyWidget';
import { SessionCompletionRateWidget } from './widgets/engagement/SessionCompletionRateWidget';
import { TimeDistributionWidget } from './widgets/engagement/TimeDistributionWidget';
import { ParticipationTrendsWidget } from './widgets/engagement/ParticipationTrendsWidget';
import { PlaceholderWidget } from './widgets/PlaceholderWidget';
import { ArticleStats } from './widgets/articles/ArticleStats';
import { ArticleViewsChart } from './widgets/articles/ArticleViewsChart';
import { TopArticles } from './widgets/articles/TopArticles';
import styles from './AnalyticsDashboard.module.scss';

/**
 * Breakpoint Detection Hook
 *
 * Detects current viewport breakpoint for responsive widget sizing.
 * Returns 'mobile', 'tablet', or 'desktop' based on viewport width.
 */
function useBreakpoint(): 'mobile' | 'tablet' | 'desktop' {
  const [breakpoint, setBreakpoint] = useState<'mobile' | 'tablet' | 'desktop'>(() => {
    if (typeof window === 'undefined') return 'mobile';
    if (window.matchMedia('(min-width: 1024px)').matches) return 'desktop';
    if (window.matchMedia('(min-width: 768px)').matches) return 'tablet';
    return 'mobile';
  });

  useEffect(() => {
    const updateBreakpoint = () => {
      if (window.matchMedia('(min-width: 1024px)').matches) {
        setBreakpoint('desktop');
      } else if (window.matchMedia('(min-width: 768px)').matches) {
        setBreakpoint('tablet');
      } else {
        setBreakpoint('mobile');
      }
    };

    updateBreakpoint();
    const mediaQueryList = window.matchMedia('(min-width: 768px)');
    mediaQueryList.addEventListener('change', updateBreakpoint);

    return () => mediaQueryList.removeEventListener('change', updateBreakpoint);
  }, []);

  return breakpoint;
}

export interface AnalyticsDashboardProps {
  data: GlobalAnalyticsResponse | null;
  isLoading?: boolean;
  error?: Error | null;
  enabledWidgets?: WidgetId[];
  widgetOrder?: WidgetId[];
  onWidgetOrderChange?: (newOrder: WidgetId[]) => void;
  isEditMode: boolean;
}

interface SortableWidgetProps {
  id: string;
  children: React.ReactNode;
  columnSpan: number;
  isEditMode: boolean;
}

function SortableWidget({ id, children, columnSpan, isEditMode }: SortableWidgetProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !isEditMode,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    gridColumn: `span ${columnSpan}`,
    touchAction: 'none',
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative' as const,
    height: '100%',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isEditMode ? listeners : {})}
      data-widget-span={columnSpan}
      className={isEditMode ? styles['sortableWidget--edit'] : ''}
    >
      {isEditMode && (
        <div className={styles['sortableWidget__overlay']}>
          <span className={styles['sortableWidget__spanIndicator']}>
            Columns: {columnSpan}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Analytics Dashboard Component
 *
 * Responsive grid layout displaying analytics widgets.
 * Supports drag-and-drop reordering and widget customization.
 */
export function AnalyticsDashboard({
  data,
  isLoading,
  error,
  enabledWidgets = [
    'overall-success-rate',
    'calendar-heatmap',
    'daily-knowledge-streak',
    'improvement-trends',
    'total-study-time',
    'session-completion-rate',
    'performance-by-type',
    'performance-by-difficulty',
    'participation-trends',
  ],
  widgetOrder,
  onWidgetOrderChange,
  isEditMode,
}: AnalyticsDashboardProps) {
  const [currentOrder, setCurrentOrder] = useState<WidgetId[]>(
    widgetOrder || enabledWidgets
  );

  useEffect(() => {
    setCurrentOrder(widgetOrder || enabledWidgets);
  }, [widgetOrder, enabledWidgets]);

  // Detect current breakpoint for responsive widget sizing
  const breakpoint = useBreakpoint();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCurrentOrder((items) => {
        const oldIndex = items.indexOf(active.id as WidgetId);
        const newIndex = items.indexOf(over.id as WidgetId);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        if (onWidgetOrderChange) {
          onWidgetOrderChange(newOrder);
        }

        return newOrder;
      });
    }
  };

  const renderWidget = (widgetId: WidgetId) => {
    switch (widgetId) {
      // --- Performance ---
      case 'overall-success-rate':
        return (
          <OverallSuccessRate
            data={data?.performance || null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'performance-by-difficulty':
        return (
          <PerformanceByDifficulty
            data={data?.performanceByDifficulty || null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'performance-by-type':
        return (
          <PerformanceByType
            data={data?.performanceByType || null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'improvement-trends':
        return (
          <ImprovementTrends
            data={data?.improvementTrends ?? null}
            isLoading={isLoading}
            error={error}
          />
        );

      case 'partial-credit-analysis':
        return <PlaceholderWidget title="Partial Credit" description="Analysis of partially correct answers" />;

      // --- Questions ---
      case 'most-challenging':
        return (
          <MostChallenging
            data={data?.mostChallenging ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'questions-mastered':
        return (
          <QuestionsMastered
            data={data?.questionsMastered ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'topic-mastery':
        return <PlaceholderWidget title="Topic Mastery" description="Breakdown by specific topic areas" />;

      // --- Engagement ---
      case 'daily-knowledge-streak':
        return (
          <DailyKnowledgeStreak
            data={data?.dailyKnowledgeStreak ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'calendar-heatmap':
        return (
          <CalendarHeatmap
            data={data?.activityHeatmap ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'total-questions-answered': {
        const sessionActivity = data?.performance ? {
          totalSessions: data.performance.sessionsCompleted,
          completedSessions: data.performance.sessionsCompleted,
          totalQuestions: data.performance.totalQuestionsAnswered,
          totalStudyTimeSeconds: data.performance.totalStudyTimeSeconds,
        } : null;

        return (
          <TotalQuestionsAnswered
            data={sessionActivity}
            isLoading={isLoading}
            error={error}
          />
        );
      }
      case 'total-study-time': {
        const sessionActivity = data?.performance ? {
          totalSessions: data.performance.sessionsCompleted,
          completedSessions: data.performance.sessionsCompleted,
          totalQuestions: data.performance.totalQuestionsAnswered,
          totalStudyTimeSeconds: data.performance.totalStudyTimeSeconds,
        } : null;

        return (
          <TotalStudyTime
            data={sessionActivity}
            isLoading={isLoading}
            error={error}
          />
        );
      }
      case 'sessions-completed':
        return (
          <SessionsCompleted
            data={data?.improvementTrends ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'time-efficiency':
        return (
          <TimeEfficiencyWidget
            data={data?.timeEfficiency ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'session-completion-rate':
        return (
          <SessionCompletionRateWidget
            data={data?.sessionCompletion ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'time-distribution':
        return (
          <TimeDistributionWidget
            data={data?.timeDistribution ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'participation-trends':
        return (
          <ParticipationTrendsWidget
            data={data?.participationTrends ?? null}
            isLoading={isLoading}
            error={error}
          />
        );


      // --- Quizlets ---
      case 'quizlet-mastery-heatmap':
        return (
          <QuizletMasteryHeatmap
            data={data?.quizletMastery ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'best-worst-performing':
        return (
          <BestWorstPerforming
            data={data?.quizletMastery ?? null}
            isLoading={isLoading}
            error={error}
          />
        );

      // --- Insights (AI) ---
      // These are placeholders for now as per plan
      case 'automated-insights':
        return <PlaceholderWidget title="Automated Insights" description="AI-generated performance analysis" />;
      case 'learning-curve':
        return <PlaceholderWidget title="Learning Curve" description="Projected mastery timeline" />;
      case 'practice-recommendations':
        return <PlaceholderWidget title="Recommended Practice" description="Suggested areas to focus on" />;
      case 'best-study-time':
        return <PlaceholderWidget title="Best Study Time" description="Optimal times for learning" />;
      case 'time-optimization':
        return <PlaceholderWidget title="Time Optimization" description="Pacing and speed analysis" />;

      // --- Articles ---
      case 'article-stats':
        return (
          <ArticleStats
            data={data?.articleAnalytics ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'article-views-chart':
        return (
          <ArticleViewsChart
            data={data?.articleAnalytics ?? null}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'top-articles':
        return (
          <TopArticles
            data={data?.articleAnalytics ?? null}
            isLoading={isLoading}
            error={error}
          />
        );

      default:
        console.warn(`Unknown widget ID: ${widgetId}`);
        return null;
    }
  };

  return (
    <div className={styles.dashboard}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={currentOrder} strategy={rectSortingStrategy}>
          <div className={`${styles.dashboard__grid} ${isEditMode ? styles['dashboard__grid--edit'] : ''}`}>
            {currentOrder.map((widgetId) => {
              // Calculate dynamic column span based on widget type and current breakpoint
              const columnSpan = getWidgetSpan(widgetId, breakpoint);

              return (
                <SortableWidget
                  key={widgetId}
                  id={widgetId}
                  columnSpan={columnSpan}
                  isEditMode={isEditMode}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      pointerEvents: isEditMode ? 'none' : 'auto',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {renderWidget(widgetId)}
                  </motion.div>
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
